/**
 * Wochentag-Editor für die Durchführungszeit einer Maßnahme.
 *
 * Bedienlogik:
 *  - Toggle "Mo–Fr gleich": nutzt ein einziges Start/Ende-Paar
 *    (Felder formData.start_time / formData.end_time). weekday_times = null.
 *  - Toggle inaktiv: zeigt 5-Zeilen-Tabelle (Mo–Fr) mit jeweils Start/Ende.
 *    Speichert formData.weekday_times = { mon: {start, end}, ... }.
 *    start_time/end_time werden geleert.
 *  - Beim Wechsel "gleich" → "abweichend" wird die aktuelle Mo-Zeit als
 *    Default für alle 5 Wochentage vorbelegt (komfortabel: nur Fr ändern).
 */
import React from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '../../components/ui/select';
import { Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SaveTemplateDialog } from './SaveTemplateDialog';

const WEEKDAYS = [
  { key: 'mon', label: 'Montag' },
  { key: 'tue', label: 'Dienstag' },
  { key: 'wed', label: 'Mittwoch' },
  { key: 'thu', label: 'Donnerstag' },
  { key: 'fri', label: 'Freitag' },
];

// Vordefinierte Zeit-Vorlagen — typische Maßnahmen-Muster bei BaE/BvB/AVGS/…
// `uniform`: einheitliche Mo–Fr Zeit (start/end). `weekdays`: pro Tag eigene Zeit.
const TIME_TEMPLATES = [
  {
    id: 'std-8h',
    label: 'Standard-Vollzeit (08:00–16:00)',
    uniform: { start: '08:00', end: '16:00' },
  },
  {
    id: 'bae-schule',
    label: 'BaE-Schulwoche (Mo–Do 7:15–16, Fr 7:15–15)',
    weekdays: {
      mon: { start: '07:15', end: '16:00' },
      tue: { start: '07:15', end: '16:00' },
      wed: { start: '07:15', end: '16:00' },
      thu: { start: '07:15', end: '16:00' },
      fri: { start: '07:15', end: '15:00' },
    },
  },
  {
    id: 'bvb-7-15',
    label: 'BvB (Mo–Do 7:30–15:30, Fr 7:30–13:00)',
    weekdays: {
      mon: { start: '07:30', end: '15:30' },
      tue: { start: '07:30', end: '15:30' },
      wed: { start: '07:30', end: '15:30' },
      thu: { start: '07:30', end: '15:30' },
      fri: { start: '07:30', end: '13:00' },
    },
  },
  {
    id: 'halbtags-avgs',
    label: 'Halbtags AVGS (08:00–12:00)',
    uniform: { start: '08:00', end: '12:00' },
  },
  {
    id: 'halbtags-pm',
    label: 'Halbtags Nachmittag (13:00–17:00)',
    uniform: { start: '13:00', end: '17:00' },
  },
];

const hasAnyWeekdayValue = (wt) => {
  if (!wt || typeof wt !== 'object') return false;
  return WEEKDAYS.some(({ key }) => {
    const v = wt[key];
    return v && (v.start || v.end);
  });
};

export const WeekdayTimesEditor = ({ formData, setFormData, locationName }) => {
  // perDay wird direkt aus formData abgeleitet (Single Source of Truth):
  //   weekday_times === null/undefined → "Mo–Fr gleich"-Modus
  //   weekday_times ist Object         → "pro Wochentag"-Modus
  const perDay = formData.weekday_times !== null && formData.weekday_times !== undefined;
  const [userTemplates, setUserTemplates] = React.useState([]);
  const [savingTpl, setSavingTpl] = React.useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);

  // User-Vorlagen aus API laden (einmalig beim Mount + bei Standort-Wechsel)
  // Filter nach Standort der Maßnahme: globale + an diesen Standort gebundene
  const locationId = formData.location_id || '';
  React.useEffect(() => {
    let cancelled = false;
    const url = locationId ? `/time-templates?location_id=${locationId}` : '/time-templates';
    api.get(url)
      .then(({ data }) => {
        if (!cancelled) setUserTemplates(Array.isArray(data) ? data : []);
      })
      .catch(() => { /* ohne Berechtigung → leer */ });
    return () => { cancelled = true; };
  }, [locationId]);

  const reloadUserTemplates = async () => {
    try {
      const url = locationId ? `/time-templates?location_id=${locationId}` : '/time-templates';
      const { data } = await api.get(url);
      setUserTemplates(Array.isArray(data) ? data : []);
    } catch {
      // ignore
    }
  };

  function togglePerDay(checked) {
    if (checked) {
      // Mo–Fr mit aktueller einheitlicher Zeit vorbelegen (oder leer)
      const base = { start: formData.start_time || '', end: formData.end_time || '' };
      const wt = {};
      WEEKDAYS.forEach(({ key }) => { wt[key] = { ...base }; });
      setFormData({ ...formData, weekday_times: wt, start_time: '', end_time: '' });
    } else {
      // Auf einheitliche Zeit zurück — übernimm Montag falls vorhanden
      const mon = (formData.weekday_times && formData.weekday_times.mon) || {};
      setFormData({
        ...formData,
        weekday_times: null,
        start_time: mon.start || formData.start_time || '',
        end_time: mon.end || formData.end_time || '',
      });
    }
  }

  const updateDay = (key, field, value) => {
    const wt = { ...(formData.weekday_times || {}) };
    wt[key] = { ...(wt[key] || { start: '', end: '' }), [field]: value };
    setFormData({ ...formData, weekday_times: wt });
  };

  const applyTemplate = (tplId) => {
    // Built-in?
    const builtin = TIME_TEMPLATES.find((t) => t.id === tplId);
    if (builtin) {
      if (builtin.uniform) {
        setFormData({
          ...formData,
          start_time: builtin.uniform.start,
          end_time: builtin.uniform.end,
          weekday_times: null,
        });
      } else if (builtin.weekdays) {
        const wt = {};
        Object.entries(builtin.weekdays).forEach(([k, v]) => { wt[k] = { ...v }; });
        setFormData({ ...formData, weekday_times: wt, start_time: '', end_time: '' });
      }
      return;
    }
    // User-Vorlage?
    const user = userTemplates.find((t) => t.id === tplId);
    if (!user) return;
    if (user.type === 'uniform' && user.uniform) {
      setFormData({
        ...formData,
        start_time: user.uniform.start || '',
        end_time: user.uniform.end || '',
        weekday_times: null,
      });
    } else if (user.type === 'weekdays' && user.weekdays) {
      const wt = {};
      Object.entries(user.weekdays).forEach(([k, v]) => { wt[k] = { ...v }; });
      setFormData({ ...formData, weekday_times: wt, start_time: '', end_time: '' });
    }
  };

  const openSaveDialog = () => {
    const hasUniform = !perDay && formData.start_time && formData.end_time;
    const hasWeekdays = perDay && hasAnyWeekdayValue(formData.weekday_times);
    if (!hasUniform && !hasWeekdays) {
      toast.error('Bitte zuerst eine Zeit eintragen, bevor du sie als Vorlage speicherst.');
      return;
    }
    setSaveDialogOpen(true);
  };

  const performSaveTemplate = async ({ name, location_id }) => {
    setSavingTpl(true);
    try {
      const hasUniform = !perDay && formData.start_time && formData.end_time;
      const body = { name, location_id: location_id || null };
      if (hasUniform) {
        body.uniform = { start: formData.start_time, end: formData.end_time };
      } else {
        body.weekdays = formData.weekday_times;
      }
      await api.post('/time-templates', body);
      toast.success(`Vorlage „${name}" gespeichert`);
      await reloadUserTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
      throw e;
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteUserTemplate = async (tpl, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Vorlage „${tpl.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/time-templates/${tpl.id}`);
      toast.success('Vorlage gelöscht');
      await reloadUserTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const formatUserTplLabel = (t) => {
    const scopeLabel = t.location_name ? ` · ${t.location_name}` : ' · Global';
    if (t.type === 'uniform' && t.uniform) {
      return `${t.name} (${t.uniform.start}–${t.uniform.end})${scopeLabel}`;
    }
    if (t.type === 'weekdays' && t.weekdays) {
      const keys = Object.keys(t.weekdays).length;
      return `${t.name} (${keys} Wochentage)${scopeLabel}`;
    }
    return `${t.name}${scopeLabel}`;
  };

  return (
    <div className="space-y-3" data-testid="weekday-times-editor">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Vorlage:</Label>
        <Select value="" onValueChange={applyTemplate}>
          <SelectTrigger className="h-9 text-xs flex-1" data-testid="time-template-select">
            <SelectValue placeholder="Zeitmuster wählen (optional) …" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel className="text-[11px] text-muted-foreground">Standard-Vorlagen</SelectLabel>
              {TIME_TEMPLATES.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`time-template-${t.id}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectGroup>
            {userTemplates.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[11px] text-muted-foreground">Eigene Vorlagen</SelectLabel>
                  {userTemplates.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={t.id}
                      data-testid={`time-template-user-${t.id}`}
                      className="pr-10"
                    >
                      <div className="flex items-center justify-between gap-2 w-full">
                        <span>{formatUserTplLabel(t)}</span>
                        <button
                          type="button"
                          onMouseDown={(e) => deleteUserTemplate(t, e)}
                          className="text-muted-foreground hover:text-destructive p-1 rounded"
                          title="Vorlage löschen"
                          data-testid={`time-template-delete-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 text-xs gap-1.5"
          onClick={openSaveDialog}
          disabled={savingTpl}
          data-testid="save-as-template-btn"
          title="Aktuelle Zeit als wiederverwendbare Vorlage speichern"
        >
          <Save className="h-3.5 w-3.5" />
          Als Vorlage speichern
        </Button>
      </div>

      <SaveTemplateDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={performSaveTemplate}
        locationId={formData.location_id || ''}
        locationName={locationName || ''}
      />

      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="per-day-toggle" className="text-xs text-muted-foreground cursor-pointer">
          {perDay ? 'Pro Wochentag unterschiedliche Zeit' : 'Mo–Fr gleiche Zeit'}
        </Label>
        <Switch
          id="per-day-toggle"
          checked={perDay}
          onCheckedChange={togglePerDay}
          data-testid="weekday-toggle"
        />
      </div>

      {!perDay ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Beginn</Label>
            <Input
              type="time"
              value={formData.start_time || ''}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              placeholder="08:00"
              data-testid="massnahme-start-time"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Ende</Label>
            <Input
              type="time"
              value={formData.end_time || ''}
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              placeholder="16:00"
              data-testid="massnahme-end-time"
            />
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="grid grid-cols-[1fr,1fr,1fr] bg-secondary/40 px-3 py-2 text-xs text-muted-foreground font-medium">
            <div>Wochentag</div>
            <div>Beginn</div>
            <div>Ende</div>
          </div>
          {WEEKDAYS.map(({ key, label }) => {
            const day = (formData.weekday_times || {})[key] || { start: '', end: '' };
            return (
              <div
                key={key}
                className="grid grid-cols-[1fr,1fr,1fr] gap-2 px-3 py-2 items-center border-t border-border"
                data-testid={`weekday-row-${key}`}
              >
                <div className="text-sm">{label}</div>
                <Input
                  type="time"
                  value={day.start || ''}
                  onChange={(e) => updateDay(key, 'start', e.target.value)}
                  data-testid={`weekday-${key}-start`}
                />
                <Input
                  type="time"
                  value={day.end || ''}
                  onChange={(e) => updateDay(key, 'end', e.target.value)}
                  data-testid={`weekday-${key}-end`}
                />
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {perDay
          ? 'Pro Wochentag eigene Durchführungszeit. Tage ohne Zeit fallen auf den 8h-Standard.'
          : 'Einheitliche Durchführungszeit für alle Werktage.'}
      </p>
    </div>
  );
};

export default WeekdayTimesEditor;
